export type GoogleAddressData = {
  formatted_address: string
  city: string
  state_code: string
  lat: number
  lng: number
  zip: string | null
}

type Coordinates = {
  lat: number
  lng: number
}

export function getAddressProperty(
  addressComponents: google.maps.GeocoderAddressComponent[],
  addressType: string,
  property: "long_name" | "short_name",
): string | null {
  const matchingComponents = addressComponents.filter((cmp) =>
    cmp.types.includes(addressType),
  )

  if (matchingComponents.length > 0) {
    return matchingComponents[0][property] || null
  }

  return null
}

export function getCityName(
  addressComponents: google.maps.GeocoderAddressComponent[],
): string | null {
  const locality = addressComponents.find((addressComponent) => {
    return addressComponent.types.includes("locality")
  })

  if (locality) {
    return locality.long_name
  }

  const adminLevel1AndPolitical = addressComponents.find((addressComponent) => {
    return includesAll(addressComponent.types, [
      "administrative_area_level_1",
      "political",
    ])
  })

  if (adminLevel1AndPolitical) {
    return adminLevel1AndPolitical.long_name
  }

  const adminLevel3AndPolitical = addressComponents.find((addressComponent) => {
    return includesAll(addressComponent.types, [
      "administrative_area_level_3",
      "political",
    ])
  })

  if (adminLevel3AndPolitical) {
    return adminLevel3AndPolitical.long_name
  }

  return null
}

export function locationContainsZipCode(
  results: google.maps.GeocoderResult[] | null,
): boolean {
  if (!results) return false
  return results.some(
    (result) =>
      getAddressProperty(
        result.address_components,
        "postal_code",
        "long_name",
      ) !== null,
  )
}

export function locationContainsStateCode(
  locations: google.maps.GeocoderResult[],
): boolean {
  return locations.some(
    (location) =>
      location.address_components.find((component) =>
        component.types.includes("administrative_area_level_1"),
      ) != null,
  )
}

export function getResultWithZipCode(
  results: google.maps.GeocoderResult[],
): google.maps.GeocoderResult {
  const resultItem = results.find((result) => {
    const zipCode = getAddressProperty(
      result.address_components,
      "postal_code",
      "long_name",
    )
    const isUSA = result.address_components.find(
      (component) => component.short_name === "US",
    )
    return zipCode !== null && isUSA != null
  })

  return resultItem ?? results[0]
}

export function getResultWithMostData(
  results: google.maps.GeocoderResult[],
): google.maps.GeocoderResult {
  return results.reduce((accumulator, current) => {
    if (
      current.address_components.length > accumulator.address_components.length
    ) {
      return current
    }
    return accumulator
  }, results[0])
}

export async function getLocationData(
  coordinates: Coordinates,
  response: google.maps.GeocoderResult[],
): Promise<google.maps.GeocoderResult> {
  try {
    const geocoder = new google.maps.Geocoder()

    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: coordinates }, function (results, status) {
        if (status === "OK") {
          if (results && results.length > 0) {
            if (locationContainsZipCode(results)) {
              resolve(getResultWithZipCode(results))
            } else if (locationContainsStateCode(results)) {
              resolve(getResultWithMostData(results))
            } else {
              resolve(response[0])
            }
          }
        }
        reject(status)
      })
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

export function normalizeAddress(address: string): string {
  return address
    .toLowerCase()
    .replace("st.", "saint")
    .replace("nyc", "new york")
    .replace("brooklyn", "new york")
}

export function cityMatchesAddress(city: string, address: string): boolean {
  return address.toLowerCase().includes(city.toLowerCase())
}

export function extractCity(cityName: string | null, address: string): string {
  if (
    cityName !== null &&
    cityMatchesAddress(normalizeAddress(cityName), normalizeAddress(address))
  ) {
    return cityName
  }
  return ""
}

export async function geocodePlaceByPlaceId(
  placeId: string,
): Promise<{
  result: google.maps.GeocoderResult
  coordinates: Coordinates
}> {
  const geocoder = new google.maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode({ placeId }, (results, status) => {
      if (status === "OK" && results && results.length > 0) {
        const result = results[0]
        const location = result.geometry.location
        const coordinates = { lat: location.lat(), lng: location.lng() }
        resolve({ result, coordinates })
      } else {
        reject(status)
      }
    })
  })
}

export function includesAll<T>(array: T[], elements: T[]): boolean {
  for (const elem of elements) {
    if (!array.includes(elem)) {
      return false
    }
  }
  return true
}
